import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Icon, { IconVariant } from "@galaxy-io/dls/icons/Icon";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import type { ImportField, ImportFile, ImportSettings } from "@gtm/contracts/imports";
import { PlusIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Inline } from "@/components/workspace/people-styles";
import { SelectField } from "@/components/workspace/select-field";
import {
  extraFields,
  fieldHint,
  fieldLabel,
  isRequired,
  missingColumns,
  primaryFields,
} from "./import-fields";
import { Issue, List, MappingRow, Section, SectionHeading, Stacked } from "./import-layout";

type Option = { value: string; label: string };
export function MappingStep({
  file,
  settings,
  sample,
  sourceOptions,
  locked,
  onChange,
  onCreateSource,
}: {
  file: ImportFile;
  settings: ImportSettings;
  /** The first data row, to show what each column holds. */
  sample: Record<string, string> | undefined;
  sourceOptions: Option[];
  locked: boolean;
  onChange: (settings: ImportSettings) => void;
  onCreateSource: () => void;
}) {
  const kind = file.kind;
  const extra = extraFields(kind);
  const [showExtra, setShowExtra] = useState(() => extra.some((f) => settings.mapping[f]));
  const fields = showExtra ? [...primaryFields[kind], ...extra] : primaryFields[kind];
  const used = new Set(Object.values(settings.mapping));
  const missing = missingColumns(kind, settings.mapping);
  const perRowSource = !!settings.mapping.source;
  function map(field: ImportField, header: string) {
    const mapping = { ...settings.mapping };
    if (header) mapping[field] = header;
    else delete mapping[field];
    onChange({ ...settings, mapping });
  }
  return (
    <>
      <Section aria-label="Source">
        <SectionHeading>
          <div>
            <Text weight={TextWeight.MEDIUM}>Source</Text>
            <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
              {perRowSource
                ? "Rows without a source name use this one. You’ll match source names next."
                : "Required. Where these rows came from; imported records keep it as provenance."}
            </Text>
          </div>
        </SectionHeading>
        <Inline>
          <SelectField
            label="Source"
            compact
            width={320}
            search
            disabled={locked}
            placeholder="Choose a source"
            value={settings.sourceId || ""}
            options={sourceOptions}
            onChange={(v) => onChange({ ...settings, sourceId: v || null })}
          />
          <Button
            label="New source"
            icon={PlusIcon}
            variant={ButtonVariant.TERTIARY}
            isDisabled={locked}
            onClick={onCreateSource}
          />
        </Inline>
      </Section>
      <Section aria-label="Columns">
        <SectionHeading>
          <div>
            <Text weight={TextWeight.MEDIUM}>Columns</Text>
            <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
              {used.size} of {file.headers.length} CSV columns matched. Unmatched columns are
              ignored.
            </Text>
          </div>
        </SectionHeading>
        {!!missing.length && (
          <div role="list" aria-label="Missing columns">
            {missing.map((m) => (
              <Issue role="listitem" key={m}>
                <Icon component={WarningCircleIcon} size={14} variant={IconVariant.WARNING} />
                <Text size={TextSize.BODY_SM} variant={TextVariant.WARNING}>
                  {m}
                </Text>
              </Issue>
            ))}
          </div>
        )}
        <List>
          <MappingRow>
            <Text size={TextSize.CAPTION} variant={TextVariant.TERTIARY}>
              GALAXY FIELD
            </Text>
            <Text size={TextSize.CAPTION} variant={TextVariant.TERTIARY}>
              CSV COLUMN
            </Text>
            <Text size={TextSize.CAPTION} variant={TextVariant.TERTIARY}>
              FIRST ROW
            </Text>
          </MappingRow>
          {fields.map((field) => {
            const header = settings.mapping[field] || "";
            const hint = fieldHint(field, kind);
            const required = isRequired(field, kind);
            return (
              <MappingRow key={field}>
                <Stacked>
                  <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM}>
                    {fieldLabel(field, kind)}
                  </Text>
                  {(hint || required) && (
                    <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
                      {[required && "Required", hint].filter(Boolean).join(" · ")}
                    </Text>
                  )}
                </Stacked>
                <SelectField
                  label={fieldLabel(field, kind)}
                  compact
                  width="100%"
                  search={file.headers.length > 8}
                  disabled={locked}
                  value={header}
                  options={[
                    { value: "", label: "Don’t import" },
                    ...file.headers.map((h) => ({
                      value: h,
                      label: used.has(h) && h !== header ? `${h} (in use)` : h,
                    })),
                  ]}
                  onChange={(v) => map(field, v)}
                />
                <Text
                  size={TextSize.BODY_SM}
                  variant={
                    header && sample?.[header] ? TextVariant.SECONDARY : TextVariant.TERTIARY
                  }
                  family={header && sample?.[header] ? FontFamily.MONO : FontFamily.SANS}
                  lineClamp={1}
                >
                  {header ? sample?.[header] || "Empty" : "—"}
                </Text>
              </MappingRow>
            );
          })}
        </List>
        {!!extra.length && (
          <div>
            <Button
              label={showExtra ? "Show fewer fields" : `Show ${extra.length} more fields`}
              variant={ButtonVariant.TERTIARY}
              size={ButtonSize.SMALL}
              onClick={() => setShowExtra(!showExtra)}
            />
          </div>
        )}
      </Section>
    </>
  );
}
